import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEFAULT_IMAGE, SITE_NAME, organisationJsonLd, pageMeta } from "@/seo";
import { trackPageView, initGA, setAnalyticsSuppressed } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

function tag(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.rel ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

/** Keeps the document head in sync with the route (prerendered HTML already carries it). */
export default function Seo() {
  const { pathname } = useLocation();
  const { user } = useAuth() || {};

  useEffect(() => { initGA(); }, []);
  useEffect(() => { setAnalyticsSuppressed(Boolean(user?.is_admin)); }, [user]);

  useEffect(() => {
    const meta = pageMeta(pathname);
    // Keep staging/platform hostnames out of search results; the canonical tag in the
    // pre-rendered HTML already points every copy at the custom domain.
    const host = window.location.hostname;
    const isCanonicalHost = host === "teachingpathways.com.au";
    const isPlatformHost = /\.emergent\.host$/i.test(host) || /\.emergentagent\.com$/i.test(host);
    if (isPlatformHost || (!isCanonicalHost && host !== "localhost")) {
      tag('meta[name="robots"]', { name: "robots", content: "noindex, nofollow" });
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }
    document.title = meta.title;
    tag('meta[name="description"]', { name: "description", content: meta.description });
    if (meta.keywords) tag('meta[name="keywords"]', { name: "keywords", content: meta.keywords });
    tag('link[rel="canonical"]', { rel: "canonical", href: meta.canonical });
    tag('meta[property="og:title"]', { property: "og:title", content: meta.title });
    tag('meta[property="og:description"]', { property: "og:description", content: meta.description });
    tag('meta[property="og:url"]', { property: "og:url", content: meta.canonical });
    tag('meta[property="og:type"]', { property: "og:type", content: "website" });
    tag('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    tag('meta[property="og:image"]', { property: "og:image", content: DEFAULT_IMAGE });
    tag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    tag('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    tag('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    tag('meta[name="twitter:image"]', { name: "twitter:image", content: DEFAULT_IMAGE });

    let ld = document.head.querySelector('script[data-seo="org"]');
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-seo", "org");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(organisationJsonLd());

    trackPageView(pathname, isPlatformHost);
  }, [pathname]);

  return null;
}
